import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';

interface CommitOptionalNumberFieldProps {
  value: number | undefined;
  onCommit: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

const CommitOptionalNumberField: React.FC<CommitOptionalNumberFieldProps> = ({
  value,
  onCommit,
  min,
  max,
  step = 1,
  placeholder = '—',
}) => {
  const initial = value === undefined || Number.isNaN(value) ? '' : String(value);
  const [draft, setDraft] = useState<string>(initial);
  const draftRef = useRef<string>(draft);
  const cancelledRef = useRef<boolean>(false);
  const commitRef = useRef<(next: string) => void>(() => {});
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  commitRef.current = (next: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (next === '') {
      if (value !== undefined) onCommit(undefined);
      return;
    }
    const v = parseFloat(next);
    if (Number.isNaN(v)) return;
    if (v === value) return;
    onCommit(v);
  };

  useEffect(
    () => () => {
      commitRef.current(draftRef.current);
    },
    [],
  );

  const commit = () => commitRef.current(draftRef.current);

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(initial);
    inputRef.current?.blur();
  };

  return (
    <Input
      ref={inputRef}
      type="number"
      value={draft}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
};

export default CommitOptionalNumberField;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #9ca3af;
  }
`;
